import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
import type { DevicePurpose } from './device-purpose';

export type AccessDeviceRegistrySortBy =
  | 'device_id'
  | 'gateway_id'
  | 'device_name'
  | 'gateway_name'
  | 'device_type'
  | 'battery'
  | 'is_active';
export type AccessDeviceSortDirection = 'asc' | 'desc';
export type AccessDeviceRegistryResolutionState = 'RESOLVED' | 'MISSING' | 'DELETED' | 'UNCERTAIN';

export interface AccessDeviceRegistryConnection {
  slot_id: string;
  membership_id: string;
  relation_revision: number | null;
  relation_row_version: number;
  property_id: string | null;
  property_name: string | null;
  room_id: string | null;
  room_no: string | null;
  slot_no: string | null;
  resolution_state: AccessDeviceRegistryResolutionState;
}

export interface AccessDeviceRegistryItem {
  /** Absent on older servers; never inferred from current connections. */
  registration_property_id?: string | null;
  /** Stored purpose, absent on older servers. */
  purpose?: DevicePurpose;
  core_device_id: string;
  provider: 'LORA';
  device_id: string;
  gateway_id: string;
  device_type: string | null;
  is_active: boolean;
  device_name: string | null;
  gateway_name: string | null;
  battery: number | null;
  revision: number;
  group_revision: number | null;
  row_version: number;
  provider_support: 'UNVERIFIED';
  slot_ids: string[];
  retired: boolean;
  created_at: string;
  updated_at: string;
  connections: AccessDeviceRegistryConnection[];
}

export interface AccessDeviceRegistryPage {
  authority: 'CORE';
  items: AccessDeviceRegistryItem[];
  next_cursor: string | null;
}

export interface AccessDeviceRegistryFilter {
  cursor?: string;
  limit?: number;
  sortBy?: AccessDeviceRegistrySortBy;
  sortDirection?: AccessDeviceSortDirection;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const exactKeys = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);
const safeInteger = (value: unknown): value is number => Number.isSafeInteger(value);
const nullableText = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';
const nullableRevision = (value: unknown): value is number | null =>
  value === null || (safeInteger(value) && value > 0);
const resolutionStates: readonly AccessDeviceRegistryResolutionState[] = [
  'RESOLVED',
  'MISSING',
  'DELETED',
  'UNCERTAIN',
];
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '장비 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });

function isConnection(value: unknown): value is AccessDeviceRegistryConnection {
  if (
    !record(value) ||
    !exactKeys(value, [
      'slot_id',
      'membership_id',
      'relation_revision',
      'relation_row_version',
      'property_id',
      'property_name',
      'room_id',
      'room_no',
      'slot_no',
      'resolution_state',
    ]) ||
    !isUuid(value.slot_id) ||
    !isUuid(value.membership_id) ||
    !nullableRevision(value.relation_revision) ||
    !safeInteger(value.relation_row_version) ||
    value.relation_row_version <= 0 ||
    !(value.property_id === null || isUuid(value.property_id)) ||
    !nullableText(value.property_name) ||
    !(value.room_id === null || isUuid(value.room_id)) ||
    !nullableText(value.room_no) ||
    !nullableText(value.slot_no) ||
    !resolutionStates.includes(value.resolution_state as AccessDeviceRegistryResolutionState)
  )
    return false;

  const hierarchy = [
    value.property_id,
    value.property_name,
    value.room_id,
    value.room_no,
    value.slot_no,
  ];
  return value.resolution_state === 'RESOLVED'
    ? value.property_id !== null && value.room_id !== null
    : hierarchy.every((field) => field === null);
}

function isRegistryItem(value: unknown): value is AccessDeviceRegistryItem {
  const keys = [
    'core_device_id',
    'provider',
    'device_id',
    'gateway_id',
    'device_type',
    'is_active',
    'device_name',
    'gateway_name',
    'battery',
    'revision',
    'group_revision',
    'row_version',
    'provider_support',
    'slot_ids',
    'retired',
    'created_at',
    'updated_at',
    'connections',
  ];
  if (
    !record(value) ||
    !exactKeys(value, [
      ...keys,
      ...(Object.hasOwn(value, 'registration_property_id') ? ['registration_property_id'] : []),
      ...(Object.hasOwn(value, 'purpose') ? ['purpose'] : []),
    ]) ||
    (Object.hasOwn(value, 'purpose') &&
      value.purpose !== 'UNKNOWN' &&
      value.purpose !== 'COMMON_ENTRANCE' &&
      value.purpose !== 'ROOM_LOCK') ||
    (Object.hasOwn(value, 'registration_property_id') &&
      value.registration_property_id !== null &&
      !(
        isUuid(value.registration_property_id) &&
        value.registration_property_id === value.registration_property_id.toLowerCase()
      )) ||
    !Array.isArray(value.slot_ids) ||
    !Array.isArray(value.connections) ||
    !value.slot_ids.every(isUuid) ||
    !value.connections.every(isConnection)
  )
    return false;

  const slots = value.slot_ids;
  const connections = value.connections;
  const normalizedSlots = new Set(slots.map((slot) => slot.toLowerCase()));
  if (
    normalizedSlots.size !== slots.length ||
    connections.length !== slots.length ||
    new Set(connections.map((connection) => connection.slot_id.toLowerCase())).size !==
      connections.length ||
    connections.some((connection) => !normalizedSlots.has(connection.slot_id.toLowerCase()))
  )
    return false;

  return (
    isUuid(value.core_device_id) &&
    value.provider === 'LORA' &&
    typeof value.device_id === 'string' &&
    value.device_id.length > 0 &&
    typeof value.gateway_id === 'string' &&
    value.gateway_id.length > 0 &&
    (value.device_type === null || typeof value.device_type === 'string') &&
    typeof value.is_active === 'boolean' &&
    (value.device_name === null || typeof value.device_name === 'string') &&
    (value.gateway_name === null || typeof value.gateway_name === 'string') &&
    (value.battery === null ||
      (safeInteger(value.battery) && value.battery >= -(2 ** 31) && value.battery < 2 ** 31)) &&
    safeInteger(value.revision) &&
    value.revision > 0 &&
    nullableRevision(value.group_revision) &&
    safeInteger(value.row_version) &&
    value.row_version > 0 &&
    value.provider_support === 'UNVERIFIED' &&
    typeof value.retired === 'boolean' &&
    isOffsetDateTime(value.created_at) &&
    isOffsetDateTime(value.updated_at) &&
    (value.retired ? slots.length === 0 : true)
  );
}

function parsePage(
  value: unknown,
  cursor: string | undefined,
  limit: number,
): AccessDeviceRegistryPage {
  if (
    !record(value) ||
    !exactKeys(value, ['authority', 'items', 'next_cursor']) ||
    value.authority !== 'CORE' ||
    !Array.isArray(value.items) ||
    value.items.length > limit ||
    !value.items.every(isRegistryItem) ||
    !(
      value.next_cursor === null ||
      (typeof value.next_cursor === 'string' && value.next_cursor.length > 0)
    ) ||
    (value.next_cursor !== null && (value.next_cursor === cursor || value.items.length === 0))
  )
    throw invalid();
  return value as unknown as AccessDeviceRegistryPage;
}

/** Read one current Core registry page without an environment selector. */
export async function readAccessDeviceRegistry(
  client: ServiceClient,
  filter: AccessDeviceRegistryFilter,
  signal?: AbortSignal,
): Promise<AccessDeviceRegistryPage> {
  const sortFields: readonly AccessDeviceRegistrySortBy[] = [
    'device_id',
    'gateway_id',
    'device_name',
    'gateway_name',
    'device_type',
    'battery',
    'is_active',
  ];
  const sorted = filter.sortBy !== undefined || filter.sortDirection !== undefined;
  if (
    (filter.cursor !== undefined &&
      (typeof filter.cursor !== 'string' || filter.cursor.length === 0)) ||
    (filter.sortBy !== undefined && !sortFields.includes(filter.sortBy)) ||
    (sorted &&
      (filter.sortBy === undefined ||
        (filter.sortDirection !== 'asc' && filter.sortDirection !== 'desc'))) ||
    (filter.limit !== undefined &&
      (!Number.isInteger(filter.limit) || filter.limit < 1 || filter.limit > 50))
  )
    throw invalid();

  const limit = filter.limit ?? 50;
  const data = await client.get<unknown>('admin/access-devices', {
    params: {
      cursor: filter.cursor,
      limit: filter.limit,
      sort_by: filter.sortBy,
      sort_direction: filter.sortDirection,
    },
    signal,
    headers: { 'Cache-Control': 'no-store' },
  });
  return parsePage(data, filter.cursor, limit);
}
